import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const PasswordStrengthIndicator = ({ password, isActive }) => {
  // Password validation checks
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  // Only show if password field is active or has content
  if (!isActive && !password) {
    return null;
  }

  const requirements = [
    { key: 'minLength', label: '8+ characters', met: checks.minLength },
    { key: 'hasUppercase', label: '1 uppercase letter', met: checks.hasUppercase },
    { key: 'hasNumber', label: '1 number', met: checks.hasNumber },
    { key: 'hasSpecialChar', label: '1 special character', met: checks.hasSpecialChar }
  ];

  // Calculate overall strength
  const metCount = Object.values(checks).filter(Boolean).length;
  let strengthLabel = '';
  let strengthColor = '';

  if (metCount === 0) {
    strengthLabel = 'Very Weak';
    strengthColor = '#d32f2f';
  } else if (metCount === 1) {
    strengthLabel = 'Weak';
    strengthColor = '#f57c00';
  } else if (metCount === 2) {
    strengthLabel = 'Fair';
    strengthColor = '#fbc02d';
  } else if (metCount === 3) {
    strengthLabel = 'Good';
    strengthColor = '#689f38';
  } else {
    strengthLabel = 'Strong';
    strengthColor = '#388e3c';
  }

  return (
    <Box
      sx={{
        mt: 1,
        mb: 2,
        p: 2,
        bgcolor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: 1,
        border: '1px solid rgba(0, 0, 0, 0.12)'
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          display: 'block',
          mb: 1,
          color: 'text.secondary'
        }}
      >
        Password Strength:{' '}
        <span style={{ color: strengthColor, fontWeight: 700 }}>
          {strengthLabel}
        </span>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {requirements.map((req) => (
          <Chip
            key={req.key}
            label={req.label}
            size="small"
            icon={req.met ? <CheckCircleIcon /> : <CancelIcon />}
            color={req.met ? 'success' : 'error'}
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: 24
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
